import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, Check, Loader2, Crown, CreditCard, Sparkles, ArrowLeft } from "lucide-react";
import { startCheckout, openBillingPortal } from "@/lib/checkout";
import { HouseholdCard } from "@/components/HouseholdCard";

export default function Settings() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => { setName(user?.name || ""); setBio(user?.bio || ""); }, [user]);

  if (!user) return null;
  const ownPremium = !!user.own_premium;
  const householdPremium = !!user.is_premium && !ownPremium;
  const isPremium = ownPremium;
  const premiumSince = user.premium_since
    ? new Date(user.premium_since).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "";

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name, bio });
      setUser(data.user);
      toast.success("Profile updated");
    } catch { toast.error("Update failed"); } finally { setSaving(false); }
  };

  const uploadAvatar = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const url = up.data.url.startsWith("http") ? up.data.url : `${BACKEND}${up.data.url}`;
      const { data } = await api.put("/auth/profile", { avatar_url: url });
      setUser(data.user);
      toast.success("Avatar updated");
    } catch { toast.error("Upload failed"); }
  };

  const manageBilling = async () => {
    setPortalLoading(true);
    const ok = await openBillingPortal();
    if (!ok) setPortalLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <button data-testid="settings-back-btn" onClick={() => navigate("/profile")}
        className="inline-flex items-center gap-2 text-sm text-nerd-light/60 hover:text-nerd-light transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to profile
      </button>

      <h1 className="font-display text-4xl font-extrabold tracking-tight mb-8">Settings</h1>

      {/* Profile settings */}
      <section data-testid="settings-profile-card" className="rounded-3xl border border-black/8 bg-nerd-card p-6 sm:p-8 mb-8 animate-fade-up">
        <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-nerd-light/40 mb-5">Profile</p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-6">
          <div className="relative">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={user.name} className="w-20 h-20 rounded-2xl object-cover border border-black/15" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-nerd-light text-white flex items-center justify-center font-display font-extrabold text-3xl">
                {(user.name || user.email).charAt(0).toUpperCase()}
              </div>
            )}
            <button data-testid="settings-avatar-upload" onClick={() => fileInput.current?.click()}
              className="absolute -bottom-2 -right-2 w-9 h-9 rounded-full bg-nerd-depth border border-black/15 flex items-center justify-center text-nerd-light hover:bg-nerd-light hover:text-white transition-colors">
              <Camera className="w-4 h-4" />
            </button>
            <input ref={fileInput} type="file" accept="image/*" className="hidden" data-testid="settings-avatar-file-input" onChange={(e) => uploadAvatar(e.target.files?.[0])} />
          </div>
          <div className="text-sm text-nerd-light/50">{user.email}</div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-nerd-light/70 mb-1.5">Display name</label>
            <input data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-mint" placeholder="Your name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-nerd-light/70 mb-1.5">Bio</label>
            <textarea data-testid="settings-bio-input" value={bio} onChange={(e) => setBio(e.target.value)}
              className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-mint resize-none" rows={3} placeholder="A short bio about you" />
          </div>
          <button data-testid="settings-save-btn" onClick={saveProfile} disabled={saving}
            className="inline-flex items-center gap-2 bg-nerd-light text-white font-semibold px-5 py-2.5 rounded-full text-sm hover:opacity-90 disabled:opacity-60 transition-opacity">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save changes
          </button>
        </div>
      </section>

      {/* Household */}
      <HouseholdCard />

      {/* Membership */}
      {householdPremium ? (
        <section data-testid="settings-membership-card" className="rounded-3xl border p-6 sm:p-7 animate-fade-up bg-nerd-light text-neutral-900 border-nerd-light">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 bg-black/5">
              <Crown className="w-6 h-6 text-neutral-900" />
            </div>
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Membership</p>
              <h2 className="font-display text-xl font-extrabold tracking-tight mt-0.5 text-neutral-900">Premium unlocked</h2>
              <p className="text-sm mt-1 text-neutral-600">
                Included through {user.household_owner_name ? <b>{user.household_owner_name}’s</b> : "your"} household — the AI chef, meal planner & supplies are all unlocked for you, free of charge.
              </p>
            </div>
          </div>
        </section>
      ) : (
      <section data-testid="settings-membership-card" className={`rounded-3xl border p-6 sm:p-7 animate-fade-up ${isPremium ? "bg-nerd-light text-neutral-900 border-nerd-light" : "bg-nerd-card border-black/8"}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:justify-between">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${isPremium ? "bg-black/5" : "bg-black/5"}`}>
              {isPremium ? <Crown className="w-6 h-6 text-neutral-900" /> : <Sparkles className="w-6 h-6 text-nerd-light" />}
            </div>
            <div>
              <p className={`text-[11px] font-mono uppercase tracking-[0.18em] ${isPremium ? "text-neutral-500" : "text-nerd-light/40"}`}>Membership</p>
              <h2 className={`font-display text-xl font-extrabold tracking-tight mt-0.5 ${isPremium ? "text-neutral-900" : ""}`}>{isPremium ? "Premium member" : "Free plan"}</h2>
              <p className={`text-sm mt-1 ${isPremium ? "text-neutral-600" : "text-nerd-light/55"}`}>
                {isPremium
                  ? `€8.99/month${premiumSince ? ` · member since ${premiumSince}` : ""} · renews automatically`
                  : "Browse & save favourites. Upgrade to unlock the AI chef, planner & supplies."}
              </p>
            </div>
          </div>
          {isPremium ? (
            <button data-testid="manage-subscription-btn" onClick={manageBilling} disabled={portalLoading}
              style={{ backgroundColor: "#0a0a0a", color: "#ffffff" }}
              className="shrink-0 inline-flex items-center justify-center gap-2 font-bold px-5 py-2.5 rounded-full text-sm hover:opacity-90 transition-opacity disabled:opacity-60">
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Manage subscription
            </button>
          ) : (
            <button data-testid="settings-upgrade-btn" onClick={startCheckout}
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-nerd-light text-white font-bold px-5 py-2.5 rounded-full text-sm hover:opacity-90 transition-opacity">
              <Crown className="w-4 h-4" /> Go Premium — €8.99/mo
            </button>
          )}
        </div>
      </section>
      )}
    </div>
  );
}
